import React, { useState, useEffect, useCallback, useContext, createContext } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom'; 
import { ThemeProvider } from './context/ThemeContext';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import MaintenanceSchedule from './components/MaintenanceSchedule';
import FaultError from './components/FaultError';
import TimeReports from './components/TimeReports';
import PersonnelManagement from './components/PersonnelManagement';
import Login from './components/Login';
import ServiceReport from './components/ServiceReport';
import MachinePriority from './components/MachinePriority';
import Settings from './components/Settings';
import DetailedServiceReport from './components/DetailedServiceReport';
import AddMachine from './components/AddMachine';
import OperatorPage from './components/OperatorPage';
import NotificationInput from './components/NotificationInput';
import EmailTest from './components/emailTest'
import ProtectedRoute from './components/ProtectedRoute';
import InventoryList from './components/InventoryList';
import InventoryForm from './components/InventoryForm';
import Graph from './components/graph';
import LogPage from './components/LogPage';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode'; // Fixed import issue
import { I18nextProvider } from 'react-i18next';
import i18n from './components/i18n';
import { LoadingContext } from "./context/LoadingContext"; // Import LoadingContext
import Spinner from "./components/Spinner"; // Import Spinner component
import QrLogin from './components/QrLogin';
import MachineList from './components/MachineList';
import MachineDetails from './components/MachineDetails';

export const ApiBaseUrlContext = createContext('');

const App = () => {
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [authToken, setAuthToken] = useState(localStorage.getItem('token') || '');
  const [loadingConfig, setLoadingConfig] = useState(true); // Track config loading state
  const [loadingAuth, setLoadingAuth] = useState(true); // Track loading state for auth token
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation(); 
  const { isLoading, setIsLoading } = useContext(LoadingContext); // Use LoadingContext

  const checkTokenExpiration = (authToken) => {
    if (!authToken) return true; // No token, considered expired
    
    const decodedToken = jwtDecode(authToken);
    const currentTime = Date.now() / 1000; // in seconds
    return decodedToken.exp < currentTime; // Token is expired if expiration time has passed
  };
  // Define a function to handle form submission
  const refreshAccessToken = async () => {
    try {
      const response = await axios.post('/mnt/api/technicians/refreshToken', {
        refreshToken: localStorage.getItem('refreshToken'), // Assuming you store the refresh token
      });

      const { token } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return token;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  };


  const handleUserActivity = useCallback(async () => {
    if (location.pathname === '/login' || location.pathname === '/fault-error') {
      console.log('On login page, skipping token check');
      return;
    }
  
    const token = localStorage.getItem('token');

    if (!token || checkTokenExpiration(token)) {
      console.log('Token expired, trying to refresh');
      const newToken = await refreshAccessToken();

      if (newToken) {
        setAuthToken(newToken);
      } else {
        setAuthToken('');
        localStorage.clear();
        navigate('/login');
      }
    }
  }, [location, navigate, setAuthToken]);

  useEffect(() => {
    // Trigger the spinner when navigation starts
    setIsLoading(true);
  
    // Simulate page load (replace this with actual loading logic if needed)
    const timer = setTimeout(() => {
      setIsLoading(false); // Stop the spinner when the page finishes loading
    }, 700); // Adjust delay if needed for API or page loading
  
    return () => clearTimeout(timer); // Cleanup the timer
  }, [location, setIsLoading]); // Dependency on location (route changes)

  useEffect(() => {
    const events = ['click', 'mousemove', 'keydown'];

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);


  useEffect(() => {
    const loadConfig = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/mnt/config.json');
        if (!response.ok) {
          throw new Error(`Failed to load config: ${response.statusText}`);
        }
        const config = await response.json();
        setApiBaseUrl(config.REACT_APP_API_BASE_URL || '');
        console.log('API Base URL loaded from config:', config.REACT_APP_API_BASE_URL);
      } catch (error) {
        console.error('Error loading config:', error);
      } finally {
        setLoadingConfig(false);
        setIsLoading(false);
      }
    };
  
    loadConfig(); // ✅ Only here once
  }, [setIsLoading]);
  

  useEffect(() => {
    if (apiBaseUrl) {
      axios.defaults.baseURL = apiBaseUrl; // Set axios baseURL
      console.log('Axios base URL set to:', apiBaseUrl);
    } else {
      console.log('apiBaseUrl is empty after loading config.');
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('Initializing authToken from localStorage:', token);
    if (token) {
      setAuthToken(token);
    }
    setLoadingAuth(false);
  }, []);

  const handleLogout = useCallback(() => {
    console.log('Logging out, clearing authToken');

    const decodedToken = jwtDecode(authToken);
    const username = decodedToken.username;
    try {
      const response = axios.post(`${apiBaseUrl}/mnt/api/technicians/logout`, {username});
      console.log(response);
    } catch (error) {
      console.log(error);
    }
    setSidebarOpen(true);
    setAuthToken('');
    localStorage.removeItem('token');
    localStorage.removeItem('user_rights');
    localStorage.removeItem('userid'); // If you're storing user ID
    delete axios.defaults.headers.common['Authorization'];
    navigate('/login');
  }, [navigate, apiBaseUrl, authToken]);

  
  useEffect(() => {
    if (authToken) {
      console.log('Setting axios Authorization header:', authToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [authToken]);

  if (
    (isLoading && location.pathname !== '/login') ||
    loadingConfig ||
    loadingAuth
  ) {
    return <Spinner />;
  }

  if (!authToken) {
    if (location.pathname === '/fault-error') {
      return <QrLogin setAuthToken={setAuthToken} />;
    }
    return <Login setAuthToken={setAuthToken} />;
  }

  
  if (loadingConfig || loadingAuth) {
    return <div>Loading...</div>;
  }

  if (!authToken) {
    if (location.pathname === '/fault-error') {
      return <QrLogin setAuthToken={setAuthToken} />;
    }
    return <Login setAuthToken={setAuthToken} />;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <ApiBaseUrlContext.Provider value={apiBaseUrl}>
        <ThemeProvider>
          <div className={`app-container ${sidebarOpen ? 'sidebar-closed' : 'sidebar-open' }`}>
            <Sidebar 
              isOpen={sidebarOpen} 
              toggleSidebar={() => setSidebarOpen(!sidebarOpen)} 
              handleLogout={handleLogout}
              currentPath={location.pathname}
            />
            <div className="content-container">
              <Routes>
                <Route path="/login" element={<Login setAuthToken={setAuthToken} />} />
                <Route path="/qr-login" element={<QrLogin setAuthToken={setAuthToken} />} /> 
                <Route
                  path="/"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Dashboard">
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/operator/:scheduleid"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Dashboard">
                      <OperatorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/maintenance-schedule"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Maintenance Schedule">
                      <MaintenanceSchedule />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fault-error"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Report Error">
                      <FaultError />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/time-reports"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Time Reports">
                      <TimeReports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/personnel"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Personnel Management">
                      <PersonnelManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-reports"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Service Reports">
                      <ServiceReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/machine-priority"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} setApiBaseUrl={setApiBaseUrl} pageName="Machine Priority">
                      <MachinePriority />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} setApiBaseUrl={setApiBaseUrl} pageName="Inventory">
                      <InventoryList />
                    </ProtectedRoute>
                  }
                />   
                <Route
                  path="/inventory/new"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} setApiBaseUrl={setApiBaseUrl} pageName="Inventory">
                      <InventoryForm />
                    </ProtectedRoute>
                  }
                />  

                <Route
                  path="/inventory/edit/:id"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} setApiBaseUrl={setApiBaseUrl} pageName="Inventory">
                      <InventoryForm />
                    </ProtectedRoute>
                  }
                />  

                <Route
                  path="/inventory/:id/logs/:logType"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} setApiBaseUrl={setApiBaseUrl} pageName="Inventory">
                      <LogPage />
                    </ProtectedRoute>
                  }
                />  


                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} setApiBaseUrl={setApiBaseUrl} pageName="Settings">
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/emailTest"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} setApiBaseUrl={setApiBaseUrl}>
                      <EmailTest />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-reports/:id"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Service Reports">
                      <DetailedServiceReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/machines"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Machines">
                      <MachineList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/machine/:machineid"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Machine Details">
                      <MachineDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-machine"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Add Machine">
                      <AddMachine />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/operator/:scheduleid"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Dashboard">
                      <OperatorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notification-input"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Notification Input">
                      <NotificationInput />
                    </ProtectedRoute>
                  }
                />
                                <Route
                  path="/graph"
                  element={
                    <ProtectedRoute authToken={authToken} handleLogout={handleLogout} pageName="Dashboard">
                      <Graph />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </div>
        </ThemeProvider>
      </ApiBaseUrlContext.Provider>
    </I18nextProvider>
  );
};

export default App;
